import React, { useState, useEffect } from 'react';
import type { User, XeroTenant } from '../types';
import { accountApi } from '../api/client';

const ADMIN_EMAIL = 'tmd@tomdavid.com.au';

interface SettingsProps {
  user: User | null;
  xeroTenants: XeroTenant[];
  xeroLoading: boolean;
  xeroNotice?: string | null;
  onSelectTenant: (tenantId: string) => void | Promise<void>;
  onDisconnectTenant: (tenantId: string) => Promise<void>;
  onRefreshXero: () => void | Promise<void>;
  onSignOut: () => void | Promise<void>;
  onConnectXeroIntent: () => Promise<void>;
  onUpdateName: (name: string) => Promise<void>;
}

type PanelKey = 'account' | 'security' | 'twofa' | 'billing' | 'xero' | 'admin';

export default function Settings({
  user,
  xeroTenants,
  xeroLoading,
  xeroNotice,
  onSelectTenant,
  onDisconnectTenant,
  onRefreshXero,
  onSignOut,
  onConnectXeroIntent,
  onUpdateName,
}: SettingsProps) {
  const isAdmin = user?.email === ADMIN_EMAIL;

  const [open, setOpen] = useState<Record<PanelKey, boolean>>({
    account: true,
    security: false,
    twofa: false,
    billing: false,
    xero: true,
    admin: false,
  });

  // Account — name editing
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(user?.name ?? '');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameSuccess, setNameSuccess] = useState(false);

  // Keep nameValue in sync when parent updates user prop (e.g. after save)
  useEffect(() => {
    if (!editingName) setNameValue(user?.name ?? '');
  }, [user?.name, editingName]);

  // Security — password change
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);

  // Xero — disconnect flow
  const [refreshing, setRefreshing] = useState(false);
  const [pendingDisconnect, setPendingDisconnect] = useState<XeroTenant | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [connectStarting, setConnectStarting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  function toggle(key: PanelKey) {
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function handleSaveName() {
    const trimmed = nameValue.trim();
    if (!trimmed) return;
    setNameSaving(true);
    setNameError(null);
    setNameSuccess(false);
    try {
      await onUpdateName(trimmed);
      setEditingName(false);
      setNameSuccess(true);
      setTimeout(() => setNameSuccess(false), 3000);
    } catch (err) {
      const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg =
        status === 429
          ? 'Too many requests. Please wait before trying again.'
          : typeof data?.error === 'string'
          ? data.error
          : err instanceof Error
          ? err.message
          : 'Could not save name. Please try again.';
      setNameError(msg);
    } finally {
      setNameSaving(false);
    }
  }

  function handleCancelNameEdit() {
    setEditingName(false);
    setNameValue(user?.name ?? '');
    setNameError(null);
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwError(null);
    setPwSuccess(false);

    if (pwNew.length < 8) {
      setPwError('New password must be at least 8 characters.');
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError('New passwords do not match.');
      return;
    }

    setPwSaving(true);
    try {
      await accountApi.changePassword(pwCurrent, pwNew);
      setPwSuccess(true);
      setPwCurrent('');
      setPwNew('');
      setPwConfirm('');
    } catch (err) {
      const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      const status = (err as { response?: { status?: number } })?.response?.status;
      let msg: string;
      if (status === 429) {
        msg = 'Too many attempts. Please wait 15 minutes before trying again.';
      } else if (data?.code === 'invalid_password') {
        msg = typeof data.message === 'string' ? data.message : 'Current password is incorrect.';
      } else {
        msg = typeof data?.error === 'string'
          ? data.error
          : err instanceof Error
          ? err.message
          : 'Could not change password. Please try again.';
      }
      setPwError(msg);
    } finally {
      setPwSaving(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await onRefreshXero();
    } finally {
      setRefreshing(false);
    }
  }

  async function handleConfirmDisconnect() {
    if (!pendingDisconnect) return;
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      await onDisconnectTenant(pendingDisconnect.tenantId);
      setPendingDisconnect(null);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (err instanceof Error ? err.message : 'Could not disconnect. Please try again.');
      setDisconnectError(msg);
    } finally {
      setDisconnecting(false);
    }
  }

  async function handleConnectXeroClick() {
    setConnectStarting(true);
    setConnectError(null);
    try {
      await onConnectXeroIntent();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (err instanceof Error ? err.message : 'Could not start Xero connection. Please try again.');
      setConnectError(msg);
      setConnectStarting(false);
    }
  }

  return (
    <div className="flex-1 px-6 py-8 lg:px-10 max-w-4xl mx-auto w-full">
      <div className="mb-8 border-b border-slate-200 pb-6">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Contractor super</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">
          Manage your account, security, and Xero connections.
        </p>
      </div>

      <div className="space-y-3">
        {/* ── Account ─────────────────────────────────────────────────── */}
        <Panel title="Account" isOpen={open.account} onToggle={() => toggle('account')}>
          {/* Email — always read-only */}
          <div className="flex items-baseline justify-between gap-4 py-1.5">
            <span className="text-xs font-medium text-slate-500">Email</span>
            <span className="truncate text-sm text-slate-900">{user?.email ?? '—'}</span>
          </div>

          {/* Name — inline editable */}
          <div className="flex items-center justify-between gap-4 py-1.5">
            <span className="text-xs font-medium text-slate-500 flex-shrink-0">Name</span>
            {editingName ? (
              <div className="flex flex-1 items-center gap-2 min-w-0">
                <input
                  type="text"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') handleCancelNameEdit();
                  }}
                  disabled={nameSaving}
                  autoFocus
                  className="flex-1 min-w-0 rounded-md border border-slate-300 px-2.5 py-1 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={handleSaveName}
                  disabled={nameSaving || !nameValue.trim()}
                  className="flex-shrink-0 rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50"
                >
                  {nameSaving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelNameEdit}
                  disabled={nameSaving}
                  className="flex-shrink-0 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-end gap-2 min-w-0">
                <span className="truncate text-sm text-slate-900">{user?.name ?? '—'}</span>
                {nameSuccess && (
                  <span className="flex-shrink-0 text-xs text-emerald-600 font-medium">Saved</span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setNameValue(user?.name ?? '');
                    setNameError(null);
                    setNameSuccess(false);
                    setEditingName(true);
                  }}
                  className="flex-shrink-0 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  Edit
                </button>
              </div>
            )}
          </div>

          {nameError && (
            <p className="mt-1 text-xs text-rose-600">{nameError}</p>
          )}

          <div className="mt-4 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={onSignOut}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Sign out
            </button>
          </div>
        </Panel>

        {/* ── Security ────────────────────────────────────────────────── */}
        <Panel title="Security" isOpen={open.security} onToggle={() => toggle('security')}>
          <p className="mb-4 text-sm text-slate-600">
            Change your account password. This updates your shared login used across all apps.
          </p>
          <form onSubmit={handleChangePassword} className="space-y-3">
            <PasswordField
              label="Current password"
              value={pwCurrent}
              onChange={setPwCurrent}
              disabled={pwSaving}
              autoComplete="current-password"
            />
            <PasswordField
              label="New password"
              value={pwNew}
              onChange={setPwNew}
              disabled={pwSaving}
              autoComplete="new-password"
              hint="At least 8 characters"
            />
            <PasswordField
              label="Confirm new password"
              value={pwConfirm}
              onChange={setPwConfirm}
              disabled={pwSaving}
              autoComplete="new-password"
            />

            {pwError && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {pwError}
              </div>
            )}
            {pwSuccess && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                Password changed successfully.
              </div>
            )}

            <div className="pt-1">
              <button
                type="submit"
                disabled={pwSaving || !pwCurrent || !pwNew || !pwConfirm}
                className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
              >
                {pwSaving ? 'Changing password…' : 'Change password'}
              </button>
            </div>
          </form>
        </Panel>

        {/* ── Two-Factor Authentication ────────────────────────────────── */}
        <Panel
          title="Two-Factor Authentication"
          isOpen={open.twofa}
          onToggle={() => toggle('twofa')}
        >
          <p className="text-sm text-slate-700">Two-factor authentication coming soon.</p>
        </Panel>

        {/* ── Plans & Billing ─────────────────────────────────────────── */}
        <Panel title="Plans & Billing" isOpen={open.billing} onToggle={() => toggle('billing')}>
          <p className="text-sm text-slate-700">Plans & billing coming soon.</p>
        </Panel>

        {/* ── Xero Connection ─────────────────────────────────────────── */}
        <Panel
          title="Xero Connection"
          subtitle="Connect your Xero organisations to import contractor reports and create pay runs."
          icon={<XeroBadge />}
          isOpen={open.xero}
          onToggle={() => toggle('xero')}
          headerRight={
            <span
              role="button"
              tabIndex={0}
              aria-disabled={refreshing || xeroLoading}
              onClick={(e) => {
                e.stopPropagation();
                if (refreshing || xeroLoading) return;
                handleRefresh();
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                e.stopPropagation();
                if (refreshing || xeroLoading) return;
                handleRefresh();
              }}
              className={`hidden sm:inline-flex select-none rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 ${
                refreshing || xeroLoading ? 'pointer-events-none opacity-50' : ''
              }`}
            >
              {refreshing || xeroLoading ? 'Refreshing…' : 'Refresh'}
            </span>
          }
        >
          {xeroTenants.length === 0 ? (
            <p className="text-sm text-slate-600">
              No Xero organisations connected yet. Connect one below to start importing
              contractor reports.
            </p>
          ) : (
            <ul className="space-y-2">
              {xeroTenants.map((t) => (
                <TenantRow
                  key={t.tenantId}
                  tenant={t}
                  onSetActive={() => onSelectTenant(t.tenantId)}
                  onDisconnect={() => {
                    setDisconnectError(null);
                    setPendingDisconnect(t);
                  }}
                />
              ))}
            </ul>
          )}

          {xeroNotice && (
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {xeroNotice}
            </div>
          )}
          {connectError && (
            <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {connectError}
            </div>
          )}
          <button
            type="button"
            onClick={handleConnectXeroClick}
            disabled={connectStarting}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-sky-500 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-sky-600 disabled:opacity-60"
          >
            <XeroDot />
            {connectStarting
              ? 'Opening Xero…'
              : xeroTenants.length === 0
              ? 'Connect Xero'
              : 'Connect another organisation'}
          </button>

          <p className="mt-3 text-xs text-slate-500">
            Add another Xero organisation to manage contractor pay runs across multiple
            accounts. Disconnecting an organisation keeps your existing records — they reappear
            if you reconnect later.
          </p>
        </Panel>

        {/* ── Admin (gated) ───────────────────────────────────────────── */}
        {isAdmin && (
          <Panel title="Admin" isOpen={open.admin} onToggle={() => toggle('admin')}>
            <p className="text-sm text-slate-700">Admin tools coming soon.</p>
          </Panel>
        )}
      </div>

      {pendingDisconnect && (
        <DisconnectModal
          tenantName={pendingDisconnect.tenantName}
          isSubmitting={disconnecting}
          errorMessage={disconnectError}
          onCancel={() => {
            if (disconnecting) return;
            setPendingDisconnect(null);
            setDisconnectError(null);
          }}
          onConfirm={handleConfirmDisconnect}
        />
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function TenantRow({
  tenant,
  onSetActive,
  onDisconnect,
}: {
  tenant: XeroTenant;
  onSetActive: () => void;
  onDisconnect: () => void;
}) {
  const connectedDate = formatConnectedDate(tenant.connectedAt);

  return (
    <li
      className={`flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 ${
        tenant.isActive
          ? 'border-emerald-200 bg-emerald-50/60'
          : 'border-slate-200 bg-white'
      }`}
    >
      <CheckCircleIcon active={tenant.isActive} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-900">{tenant.tenantName}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
          {connectedDate && <span>Connected {connectedDate}</span>}
          {!tenant.hasPayrollAccess && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
              No payroll access
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        {tenant.isActive ? (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700">
            <LinkIcon />
            Active
          </span>
        ) : (
          <button
            type="button"
            onClick={onSetActive}
            className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Set as active
          </button>
        )}
        <button
          type="button"
          onClick={onDisconnect}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50"
        >
          <UnlinkIcon />
          Disconnect
        </button>
      </div>
    </li>
  );
}

function DisconnectModal({
  tenantName,
  isSubmitting,
  errorMessage,
  onCancel,
  onConfirm,
}: {
  tenantName: string;
  isSubmitting: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-100 px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-500">
              <UnlinkIcon large />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-slate-900">
                Disconnect Xero organisation?
              </h2>
              <p className="mt-0.5 text-sm text-slate-500 truncate">{tenantName}</p>
            </div>
          </div>
        </div>

        <div className="space-y-3 px-6 py-5 text-sm text-slate-700">
          <p>
            This will disconnect <strong>{tenantName}</strong> from your shared Xero connection.
            It may stop Xero access in InvoicePolish, Receipts, and Contractor Super.
          </p>
          <p>
            Your existing records and app data will be retained. If you reconnect this
            organisation later, your previous data will reappear.
          </p>
          <p>
            You will not be able to fetch new data from Xero until the organisation is
            reconnected.
          </p>

          {errorMessage && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {errorMessage}
            </div>
          )}
        </div>

        <div className="flex gap-3 border-t border-slate-100 px-6 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="flex-1 rounded-md bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
          >
            {isSubmitting ? 'Disconnecting…' : 'Disconnect'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Primitives ──────────────────────────────────────────────────────────────

function PasswordField({
  label,
  value,
  onChange,
  disabled,
  autoComplete,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  autoComplete?: string;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      {hint && <span className="ml-2 text-[11px] text-slate-400">{hint}</span>}
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        autoComplete={autoComplete}
        className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 disabled:opacity-60"
      />
    </label>
  );
}

function Panel({
  title,
  subtitle,
  icon,
  isOpen,
  onToggle,
  children,
  headerRight,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-slate-50"
      >
        {icon}
        <div className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-slate-900">{title}</span>
          {subtitle && <span className="mt-0.5 block text-xs text-slate-500">{subtitle}</span>}
        </div>
        {headerRight}
        <Chevron open={isOpen} />
      </button>
      {isOpen && <div className="border-t border-slate-200 px-5 py-4">{children}</div>}
    </div>
  );
}

function formatConnectedDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-4 w-4 flex-shrink-0 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`}
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function XeroBadge() {
  return (
    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-sky-500 text-[10px] font-bold uppercase tracking-wide text-white">
      xero
    </span>
  );
}

function XeroDot() {
  return (
    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/25 text-[9px] font-bold uppercase tracking-wide text-white">
      x
    </span>
  );
}

function CheckCircleIcon({ active }: { active: boolean }) {
  return (
    <svg
      className={`h-5 w-5 flex-shrink-0 ${active ? 'text-emerald-500' : 'text-slate-400'}`}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg className="h-3.5 w-3.5 text-slate-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M11.354 4.646a3.5 3.5 0 014.95 4.95l-2.122 2.121a.75.75 0 11-1.06-1.06l2.121-2.122a2 2 0 10-2.828-2.828L10.293 7.83a.75.75 0 11-1.06-1.06l2.121-2.122zM8.646 15.354a3.5 3.5 0 01-4.95-4.95l2.122-2.121a.75.75 0 011.06 1.06L4.757 11.464a2 2 0 102.828 2.828l2.122-2.121a.75.75 0 011.06 1.06l-2.121 2.122z" />
      <path d="M7.464 12.536a.75.75 0 010-1.06l4.072-4.072a.75.75 0 011.06 1.06l-4.072 4.072a.75.75 0 01-1.06 0z" />
    </svg>
  );
}

function UnlinkIcon({ large }: { large?: boolean } = {}) {
  const cls = large ? 'h-5 w-5' : 'h-3.5 w-3.5';
  return (
    <svg className={cls} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M11.354 4.646a3.5 3.5 0 014.95 4.95l-1.06 1.06a.75.75 0 11-1.061-1.06l1.06-1.06a2 2 0 10-2.828-2.829l-1.06 1.06a.75.75 0 01-1.061-1.06l1.06-1.06zM8.646 15.354a3.5 3.5 0 01-4.95-4.95l1.06-1.06a.75.75 0 011.061 1.06l-1.06 1.06a2 2 0 102.828 2.829l1.06-1.06a.75.75 0 011.061 1.06l-1.06 1.06z" />
      <path fillRule="evenodd" d="M2.22 2.22a.75.75 0 011.06 0l14.5 14.5a.75.75 0 11-1.06 1.06L2.22 3.28a.75.75 0 010-1.06z" clipRule="evenodd" />
    </svg>
  );
}
